/**
 * Antivirus verdict on a file's current version.
 * Serialized as a number on file entries; as a name string on WS / admin APIs.
 *
 * Semantics (aligned with BE):
 * - None: no scan yet / not applicable
 * - Pending: scan queued or in progress
 * - Clean: no threat found
 * - Infected: threat found (blocks content actions when threat name present)
 * - Error: scan failed; may be retryable depending on error code
 * - Skipped: scan not run (policy / extension filter)
 * - TooLarge: file over MaxScanSizeBytes or STREAM_TOO_LARGE — not verified;
 *   retry would same-fail; do not offer retry; does not block actions
 */
export enum AntivirusScanStatusEnum {
  None = 0,
  Pending = 1,
  Clean = 2,
  Infected = 3,
  Error = 4,
  Skipped = 5,
  TooLarge = 6,
}

export type AntivirusScanStatusName =
  'None' | 'Pending' | 'Clean' | 'Infected' | 'Error' | 'Skipped' | 'TooLarge';

const NAME_TO_STATUS: Record<string, AntivirusScanStatusEnum> = {
  none: AntivirusScanStatusEnum.None,
  pending: AntivirusScanStatusEnum.Pending,
  clean: AntivirusScanStatusEnum.Clean,
  infected: AntivirusScanStatusEnum.Infected,
  error: AntivirusScanStatusEnum.Error,
  skipped: AntivirusScanStatusEnum.Skipped,
  toolarge: AntivirusScanStatusEnum.TooLarge,
};

/** Map WS / admin status name → numeric enum. Unknown → undefined. */
export function antivirusScanStatusFromName(
  name: string | undefined | null,
): AntivirusScanStatusEnum | undefined {
  if (name == null || typeof name !== 'string') return undefined;
  return NAME_TO_STATUS[name.trim().toLowerCase()];
}

export function antivirusScanStatusToName(
  status: AntivirusScanStatusEnum,
): AntivirusScanStatusName {
  return AntivirusScanStatusEnum[status] as AntivirusScanStatusName;
}
